import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import composeRefs from '@seznam/compose-react-refs/composeRefs';
import clsx from 'clsx';
import autosizeLib from 'autosize';

import { useDelegateFocus, useFocus } from '../../common/hooks';
import {
  DivPropsWithoutRef,
  IntrinsicPropsWithoutRef
} from '../../common/types';

export interface TextAreaProps
  extends Omit<IntrinsicPropsWithoutRef<'textarea'>, 'onClick' | 'className'> {
  /**
   * Состояние TextArea с ошибкой
   */
  error?: boolean;
  /**
   * TextArea на полную ширину родительского элемента
   */
  fullWidth?: boolean;
  /**
   * TextArea с возможностью подстраивать высоту компонента под высоту текста
   */
  autosize?: boolean;
  /**
   * Значение счетчика в углу поля.
   * Может быть использован как счетчик оставшегося числа символов,
   * так и как счетчик введенного количества символов, слов и т.п.
   * Требуемое поведение счетчика необходимо реализовать самостоятельно
   * при использовании компонента
   */
  counter?: React.ReactNode;
  /**
   * Отображает счетчик красным цветом, чтобы сфокусировать на нём внимание
   * в случае нарушения ограничения.
   */
  counterError?: boolean;
  /**
   * В каком направлении разрешить изменение размера компонента.
   * Значения соответствуют css-свойству resize.
   * По-умолчанию позволяет изменение размера только по вертикали
   * только если autosize=false.
   * Не стоит задавать это свойство совместно с autosize=true.
   * Изменение размера по горизонтали не поддерживается, см. документацию.
   */
  resize?: 'none' | 'vertical';
  /**
   * Ref на textarea-элемент
   */
  inputRef?: React.Ref<HTMLTextAreaElement>;
  /**
   * Реф на корневой элемент
   */
  innerRef?: React.Ref<HTMLDivElement>;
  /**
   * Обработчик нажатия на TextArea
   */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Дополнительные классы элементов компонента:
   * * root - внешний div
   * * textArea - элемент textarea
   * * counter - счетчик в углу поля
   */
  classes?: {
    root?: string;
    textArea?: string;
    counter?: string;
  };
  /**
   * Дополнительный css-класс корневого элемента
   */
  className?: string;
  /**
   * Дополнительные пропы корневого элемента
   */
  rootProps?: DivPropsWithoutRef;
  /**
   * Дополнительные стили элемента textarea
   */
  inputStyle?: React.CSSProperties;
  /**
   * Значение TextArea в контролируемом варианте
   */
  value?: string;
  /**
   * Значение TextArea по умолчанию в неконтролируемом варианте
   */
  defaultValue?: string;
  /**
   * Максимальное количество символов допустимое для ввода в текстовое поле.
   * По умолчанию включает отображения счетчика оставшихся символов.
   * Чтобы его отключить, передайте counter=false
   */
  maxLength?: number;
}

/**
 * @deprecated используйте TextAreaProps
 */
export type ITextAreaProps = TextAreaProps;

const getMaxLengthCounter = (value: string, maxLength: number) =>
  maxLength - value.length;

export const TextArea: React.FC<TextAreaProps> = ({
  inputRef: inputRefProp,
  counter: counterProp,
  error,
  fullWidth,
  autosize,
  onClick,
  className,
  classes,
  style,
  rootProps,
  innerRef,
  resize,
  inputStyle,
  counterError: counterErrorProp,
  maxLength,
  value: valueProp,
  defaultValue,
  onChange,
  ...textAreaProps
}) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Autosize
  useLayoutEffect(() => {
    if (autosize && inputRef.current) {
      autosizeLib(inputRef.current);
      return () => {
        if (inputRef.current) {
          autosizeLib.destroy(inputRef.current);
        }
      };
    }
  }, [autosize]);

  useEffect(() => {
    if (autosize && inputRef.current) {
      autosizeLib.update(inputRef.current);
    }
  });
  // /Autosize

  // Focus
  const delegateProps = useDelegateFocus<HTMLDivElement, HTMLTextAreaElement>(
    inputRef,
    { onClick }
  );
  const { focused, ...focusProps } = useFocus(textAreaProps);
  // /Focus

  // Value
  const [value, setValue] = useState(valueProp || defaultValue || '');
  useEffect(() => {
    setValue(valueProp || defaultValue || '');
  }, [valueProp, defaultValue]);
  // /Value

  // Counter
  const getCounter = useCallback(() => {
    if (typeof counterProp !== 'undefined') {
      return counterProp;
    } else if (typeof maxLength !== 'undefined') {
      return getMaxLengthCounter(value, maxLength);
    }
    return undefined;
  }, [counterProp, value, maxLength]);

  const [counter, setCounter] = useState<React.ReactNode | undefined>(
    getCounter
  );

  useEffect(() => {
    setCounter(getCounter());
  }, [getCounter]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (typeof valueProp === 'undefined') {
        setValue(e.target.value);
      }
      onChange?.(e);
    },
    [onChange, valueProp]
  );

  const hasCounter = typeof counter !== 'undefined' && counter !== false;
  const maxLengthError =
    typeof counterProp === 'undefined' &&
    typeof maxLength !== 'undefined' &&
    typeof counter === 'number' &&
    counter < 0;
  const counterError = counterErrorProp ?? maxLengthError;
  // /Counter

  return (
    <div
      ref={innerRef}
      {...rootProps}
      className={clsx(className, classes?.root, 'hse-TextArea hse-Input', {
        'hse-TextArea_error hse-Input_error': error,
        'hse-TextArea_disabled hse-Input_disabled': textAreaProps.disabled,
        'hse-TextArea_fullWidth hse-Input_fullWidth': fullWidth,
        'hse-TextArea_focus hse-Input_focus': focused,
        'hse-TextArea_autosize': autosize
      })}
      style={{ ...style, ...rootProps?.style }}
      {...delegateProps}
    >
      <textarea
        className={clsx(classes?.textArea, 'hse-TextArea__textarea', {
          'hse-TextArea__textarea_autosize': autosize,
          'hse-TextArea__textarea_disabled': textAreaProps.disabled,
          'hse-TextArea__textarea_hasCounter': hasCounter
        })}
        ref={composeRefs(inputRef, inputRefProp)}
        style={{ resize, ...inputStyle }}
        value={value}
        defaultValue={defaultValue}
        maxLength={maxLength}
        onChange={handleChange}
        {...textAreaProps}
        {...focusProps}
      />
      {hasCounter && (
        <div
          className={clsx('hse-TextArea__counter', classes?.counter, {
            'hse-TextArea__counter_error': counterError
          })}
        >
          {counter}
        </div>
      )}
    </div>
  );
};
